import { createApp, h, reactive } from 'vue'
import { showError, showSuccess, showInfo, showWarning } from './toast'

interface AlertModalState {
  show: boolean
  message: string
  type: 'alert' | 'confirm' | 'prompt'
  title?: string
  defaultValue?: string
  resolve?: (value: any) => void
}

const alertModalState: AlertModalState = {
  show: false,
  message: '',
  type: 'alert',
  title: 'Notification',
  defaultValue: '',
  resolve: undefined
}

// Create the modal component
const AlertModal = {
  setup() {
    // Make state reactive
    const state = reactive(alertModalState)
    
    const handleConfirm = () => {
      state.show = false
      if (state.resolve) {
        if (state.type === 'prompt') {
          state.resolve(state.defaultValue)
        } else {
          state.resolve(state.type === 'confirm' ? true : undefined)
        }
      }
    }
    
    const handleCancel = () => {
      state.show = false
      if (state.resolve) {
        state.resolve(state.type === 'confirm' ? false : undefined)
      }
    }
    
    const handleInput = (event: Event) => {
      state.defaultValue = (event.target as HTMLInputElement).value
    }
    
    return () => h('div', {
      class: [
        'fixed inset-0 z-50 flex items-center justify-center',
        { 'hidden': !state.show }
      ],
      style: { backgroundColor: 'rgba(0, 0, 0, 0.5)' }
    }, [
      h('div', {
        class: 'bg-white rounded-lg shadow-xl max-w-md w-full mx-4'
      }, [
        // Header
        h('div', { class: 'px-6 py-4 border-b border-gray-200' }, [
          h('h3', { class: 'text-lg font-medium text-gray-900' }, state.title)
        ]),
        
        // Body
        h('div', { class: 'px-6 py-4' }, [
          h('p', { class: 'text-sm text-gray-600' }, state.message),
          
          // Input for prompt
          state.type === 'prompt' ? h('input', {
            type: 'text',
            value: state.defaultValue,
            onInput: handleInput,
            class: 'mt-3 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
          }) : null
        ]),
        
        // Footer
        h('div', { class: 'px-6 py-4 border-t border-gray-200 flex justify-end space-x-3' }, [
          // Cancel button for confirm/prompt
          state.type !== 'alert' ? h('button', {
            onClick: handleCancel,
            class: 'px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500'
          }, state.type === 'confirm' ? 'Cancel' : 'Cancel') : null,
          
          // OK/Confirm button
          h('button', {
            onClick: handleConfirm,
            class: 'px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500'
          }, state.type === 'confirm' ? 'OK' : 'OK')
        ])
      ])
    ])
  }
}

// Mount the modal to the DOM
export function mountAlertModal() {
  // Create container if it doesn't exist
  let container = document.getElementById('alert-modal-container')
  if (!container) {
    container = document.createElement('div')
    container.id = 'alert-modal-container'
    document.body.appendChild(container)
  }
  
  // Create and mount the app
  const app = createApp(AlertModal)
  app.mount(container)
}

// Override browser alert functions
export function overrideBrowserAlerts() {
  // Store original functions
  const originalAlert = window.alert
  const originalConfirm = window.confirm
  const originalPrompt = window.prompt
  
  // Override alert
  window.alert = function(message?: string): void {
    console.log('Alert called with message:', message)
    showInfo(message || '')
  }
  
  // Override confirm
  window.confirm = function(message?: string): boolean {
    alertModalState.type = 'confirm'
    alertModalState.message = message || ''
    alertModalState.title = 'Confirm'
    alertModalState.show = true
    
    // Create promise for async behavior
    return new Promise((resolve) => {
      alertModalState.resolve = resolve
    }) as any
  }
  
  // Override prompt
  window.prompt = function(message?: string, defaultValue?: string): string | null {
    alertModalState.type = 'prompt'
    alertModalState.message = message || ''
    alertModalState.title = 'Input'
    alertModalState.defaultValue = defaultValue || ''
    alertModalState.show = true
    
    // Create promise for async behavior
    return new Promise((resolve) => {
      alertModalState.resolve = resolve
    }) as any
  }
  
  // Return function to restore original behavior
  return () => {
    window.alert = originalAlert
    window.confirm = originalConfirm
    window.prompt = originalPrompt
  }
}

// Initialize on import
if (typeof window !== 'undefined') {
  mountAlertModal()
  overrideBrowserAlerts()
}